import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import ChangePasswordModal from './ChangePasswordModal';
import SSOUnlinkModal from './SSOUnlinkModal';
import Setup2FAModal from './Setup2FAModal';
import { GetUserData, logoutWithAudit } from '../../redux/usersSlice';
import axiosInstance from '../../apiServices/axios';
import styles from './css/UserSettings.module.css';

const UserSettings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, access } = useSelector(state => state.user);
    const [isLinking, setIsLinking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showSSOUnlinkModal, setShowSSOUnlinkModal] = useState(false);
    const [showSetup2FAModal, setShowSetup2FAModal] = useState(false);
    /** When true, setup-2fa must send `update: true` (backend: clear old MFA + workspace audit). Set only when opening the modal. */
    const [mfaModalIsUpdate, setMfaModalIsUpdate] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const isLinked = user?.microsoft_linked;
    const linkedEmail = user?.microsoft_email;

    const handleLinkMicrosoft = async () => {
        setIsLinking(true);
        try {
            // Call the backend API to get the proper Azure auth URL
            const response = await axiosInstance.post('accounts/settings/link-azure/');

            // Redirect to the Azure auth URL provided by the backend
            if (response.data.azure_auth_url) {
                window.location.href = response.data.azure_auth_url;
            } else {
                throw new Error('No Azure auth URL received from server');
            }
        } catch (error) {
            console.error('Error linking Microsoft account:', error);
            const errorMessage = error.response?.data?.error || 'Failed to initiate Microsoft account linking';
            toast.error(errorMessage);
            setIsLinking(false);
        }
    };

    const handleUnlinkMicrosoft = () => {
        setShowSSOUnlinkModal(true);
    };

    const handleUsePasswordInstead = () => {
        setShowSSOUnlinkModal(true);
    };

    const handlePasswordSubmit = async e => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosInstance.post('accounts/settings/unlink-azure/', {
                new_password: newPassword,
            });

            const data = response.data;

            // Check if logout is required
            if (data.logout_required) {
                toast.success('Microsoft account unlinked successfully. You have been logged out.');
                await dispatch(logoutWithAudit());

                // Clear any stored tokens/auth data
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('persist:root');

                // Redirect to login page
                navigate('/login');
                return;
            }

            // If no logout required, refresh user data
            await dispatch(GetUserData());
            toast.success('Microsoft account unlinked successfully. You can now login with email and password.');

            // Reset form
            setShowPasswordForm(false);
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error('Error unlinking Microsoft account:', error);
            const errorMessage = error.response?.data?.error || 'Failed to unlink Microsoft account';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelUnlink = () => {
        setShowPasswordForm(false);
        setNewPassword('');
        setConfirmPassword('');
    };

    // Check for success parameter in URL (after Microsoft callback)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const linkedStatus = urlParams.get('linked');
        const errorReason = urlParams.get('reason');

        if (linkedStatus === 'success') {
            toast.success('Microsoft account linked successfully!');
            // Remove the parameter from URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Refresh user data to get updated Microsoft account status
            dispatch(GetUserData());
        } else if (linkedStatus === 'error') {
            // Display specific error message based on reason
            let errorMessage = 'Failed to link Microsoft account';
            if (errorReason === 'email_mismatch') {
                errorMessage = "Your Microsoft account doesn't match your registered Lens email. Please use the correct account or contact support.";
            } else if (errorReason === 'account_already_linked') {
                errorMessage = 'This Microsoft account is already linked to another user';
            } else if (errorReason === 'invalid_code') {
                errorMessage = 'Invalid authorization code. Please try again';
            } else if (errorReason === 'network_error') {
                errorMessage = 'Network error occurred. Please try again';
            }

            toast.error(errorMessage);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [dispatch]);

    return (
        <div className={styles.editBody}>
            <div className="row m-0 p-0">
                <div className="col-sm-12 col-md-2 m-0 p-0">
                    <div className={styles.navLinkBody}>
                        <div className={styles.selectednavItem + ' ' + styles.navLinkItem}>Account Management</div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-1 m-0 p-0"></div>
                <div className="col-sm-12 col-md-4 m-0 p-0">
                    <div id="containerElement" className={styles.formBodies}>
                        <h1 className={styles.settingsTitle}>Account Management</h1>

                        <div>
                            <h2 className={styles.sectionTitle}>Two-factor authentication</h2>
                            <div className={styles.methodBody + ' ps-0 pt-0'}>
                                <p style={{ marginBottom: '16px', color: 'var(--Neutral-Gray-7)' }}>
                                    {user?.has_mfa
                                        ? 'Update your two-factor authentication method. You can switch between authenticator app and SMS.'
                                        : 'Add an extra layer of security by requiring a verification code when you sign in.'}
                                </p>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => {
                                        setMfaModalIsUpdate(Boolean(user?.has_mfa));
                                        setShowSetup2FAModal(true);
                                    }}>
                                    {user?.has_mfa ? 'Update 2FA' : 'Set up 2FA'}
                                </button>
                            </div>
                        </div>

                        <div>
                            <h2 className={styles.sectionTitle}>Sign in method</h2>

                            {/* Password + 2FA Method */}
                            <div
                                className={styles.methodBody}
                                style={{
                                    border: !isLinked && '1px solid var(--Index-Value-5, #69b34c) ',
                                }}>
                                {!isLinked && (
                                    <div className={styles.statusBadge}>
                                        <span>Active</span>
                                    </div>
                                )}
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className={styles.methodName}>Password + 2FA</span>
                                    {isLinked ? (
                                        <DoubleConfirmationModal
                                            message="Are you sure you want to switch to password authentication? You will need to set a new password for email/password login and current SSO will be unlinked."
                                            handleAction={handleUsePasswordInstead}
                                            modalOneBtnL="Use Password Instead"
                                            modalOneBtnR="Cancel"
                                            modalTwoBtnL="Cancel"
                                            modalTwoBtnR="Use Password Instead"
                                            type="warning">
                                            <button className={styles.actionButton}>Use Password Instead</button>
                                        </DoubleConfirmationModal>
                                    ) : (
                                        <button className={styles.actionButton} onClick={() => setShowChangePasswordModal(true)}>
                                            Change Password
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Microsoft SSO Method */}
                            <div
                                className={styles.methodBody}
                                style={{
                                    border: isLinked && '1px solid var(--Index-Value-5, #69b34c) ',
                                }}>
                                {isLinked && (
                                    <div className={styles.statusBadge}>
                                        <span>Active</span>
                                    </div>
                                )}
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className={styles.methodName}>Microsoft Single Sign On + 2FA</span>
                                    {isLinked ? (
                                        <DoubleConfirmationModal
                                            message="Are you sure you want to unlink your Microsoft account? You will need to set a new password for email/password login."
                                            handleAction={handleUnlinkMicrosoft}
                                            modalOneBtnL="Unlink Account"
                                            modalOneBtnR="Cancel"
                                            modalTwoBtnL="Cancel"
                                            modalTwoBtnR="Unlink Account"
                                            type="warning">
                                            <button className={styles.actionButton} disabled={isLoading}>
                                                {isLoading ? 'Unlinking...' : 'Unlink Account'}
                                            </button>
                                        </DoubleConfirmationModal>
                                    ) : (
                                        <button className={styles.actionButton} onClick={handleLinkMicrosoft} disabled={isLinking}>
                                            {isLinking ? 'Linking...' : 'Link Account'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Password Form for Unlinking */}
                            {showPasswordForm && (
                                <div className={styles.passwordForm}>
                                    <div className={styles.passwordFormHeader}>
                                        <h6 className="mb-0 fw-bold">
                                            <i className="fas fa-key me-2"></i>
                                            Set New Password
                                        </h6>
                                    </div>
                                    <div className={styles.passwordFormBody}>
                                        <p style={{ color: '#856404', marginBottom: '24px' }}>
                                            <i className="fas fa-info-circle me-2"></i>
                                            To unlink your Microsoft account, you need to set a new password for email/password login.
                                        </p>
                                        <form onSubmit={handlePasswordSubmit}>
                                            <div>
                                                <label className={styles.formLabel}>
                                                    <i className="fas fa-lock me-2"></i>
                                                    New Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className={styles.formControl}
                                                    value={newPassword}
                                                    onChange={e => setNewPassword(e.target.value)}
                                                    placeholder="Enter new password"
                                                    required
                                                    minLength="8"
                                                />
                                                <div className={styles.formText}>
                                                    <i className="fas fa-shield-alt me-1"></i>
                                                    Password must be at least 8 characters long
                                                </div>
                                            </div>
                                            <div>
                                                <label className={styles.formLabel}>
                                                    <i className="fas fa-check-circle me-2"></i>
                                                    Confirm Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className={styles.formControl}
                                                    value={confirmPassword}
                                                    onChange={e => setConfirmPassword(e.target.value)}
                                                    placeholder="Confirm new password"
                                                    required
                                                />
                                            </div>
                                            <div className={styles.buttonGroup}>
                                                <button type="submit" className={styles.btnDanger} disabled={isLoading}>
                                                    {isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Unlinking...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-unlink me-2"></i>
                                                            Unlink Account & Set Password
                                                        </>
                                                    )}
                                                </button>
                                                <button type="button" className={styles.btnOutline} onClick={handleCancelUnlink} disabled={isLoading}>
                                                    <i className="fas fa-times me-2"></i>
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <ChangePasswordModal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />

            {/* SSO Unlink Modal */}
            <SSOUnlinkModal isOpen={showSSOUnlinkModal} onClose={() => setShowSSOUnlinkModal(false)} />

            {/* Setup 2FA Modal */}
            <Setup2FAModal
                isOpen={showSetup2FAModal}
                onClose={() => setShowSetup2FAModal(false)}
                userEmail={user?.email}
                isUpdate={mfaModalIsUpdate}
            />
        </div>
    );
};

export default UserSettings;
